/*!
 * mm-engine.js — the mashup maker's pipeline, from two AudioBuffers to tracks
 * placed on one timeline. No DOM: the page (mm-app.js) owns the screen, this
 * owns the order of the work and the memory.
 *
 *   var E = MashupEngine.create({ slug, ctx });
 *   E.setSong('a'|'b'|'c', { buffer, file, name, isStem: bool })  → analysis promise (grid + key);
 *     the buffer is kept as decoded (one copy per song, one per FILE across slots — E.bufferFor)
 *   E.build({ signal, onStage(stage, p, info), onBeat?, external? })    → plan + tracks
 *     onBeat() — the separator ran its model once more (a sign of life between progress steps)
 *     external: { a?, b?, c? } — promises of stems separated elsewhere (the server lane, mm-server.js);
 *     E.wantStems(k) says which ones the slot needs
 *   E.replan({ tempoMode, bpm, forceShift, shiftPolicy, forceBar, forceRegion }) → re-render what changed
 *   E.replanC({ forceShift, forceBar, forceRegion }) → the second vocal alone
 *   E.tracks() → { vocal:{buffer, at}, vocal2?:{buffer, at}, stems:{…}, duration } (for MashupMixer)
 *   E.plan(), E.planC(), E.song('a'|'b'|'c')
 *
 * THREE songs: C is a second singer over the same music (MashupPlan.planSecond). Its tempo and the
 * band's key come from A's plan, so C's shift is its own vocal's only; by default C comes in right
 * after A's fragment (A takes the first half of the music left after its landing, C the rest). Moved
 * or cut by the person, C stays where they put it. Only C's vocals are separated — one stem.
 *
 * Stems outlive a rebuild: they depend on the file alone, so a tempo fix, a third song or the same
 * pair built again separates nothing twice. A slot that takes a new file drops its own (dropStems).
 *
 * Order and memory: the two songs are separated ONE AFTER ANOTHER in the MUSION
 * worker, and the model is released right after the second (worker.terminate is
 * the only thing that returns wasm memory — measured, CLAUDE.md). The stems stay
 * as AudioBuffers: a 4-min song is 4 × 85 MB of float stereo, so a phone gets the
 * needed set only (A vocals, B drums/bass/other/vocals).
 */
(function (root) {
    'use strict';

    var P = function () { return root.MashupPlan; };
    var OC = root.OfflineAudioContext || root.webkitOfflineAudioContext;
    var STEMS = ['vocals', 'drums', 'bass', 'other'];

    function abort(signal) { if (signal && signal.aborted) { var e = new Error('cancelled'); e.name = 'AbortError'; throw e; } }

    function makeBuffer(ch, len, sr) {
        try { return new AudioBuffer({ numberOfChannels: ch, length: Math.max(1, len), sampleRate: sr }); }
        catch (_) { return new OC(ch, Math.max(1, len), sr).createBuffer(ch, Math.max(1, len), sr); }
    }
    function toRate(ab, sr) {
        if (!ab || ab.sampleRate === sr) return Promise.resolve(ab);
        var off = new OC(Math.max(1, Math.min(2, ab.numberOfChannels)), Math.ceil(ab.duration * sr), sr);
        var s = off.createBufferSource(); s.buffer = ab; s.connect(off.destination); s.start();
        return off.startRendering();
    }
    function stereo(ab) {
        if (ab.numberOfChannels >= 2) return ab;
        var out = makeBuffer(2, ab.length, ab.sampleRate);
        out.getChannelData(0).set(ab.getChannelData(0)); out.getChannelData(1).set(ab.getChannelData(0));
        return out;
    }
    /**
     * A stem at 16 bits — what a device short of memory keeps of every stem the studio does not PLAY
     * (create -> packIdle): half the bytes of float. The scale keeps a stem that overshoots full scale
     * (a separator's output can) whole; the step, 1/32767 of it, is far under the 16-bit file it ends in.
     */
    function packStem(ab) {
        var n = ab.length, ch = ab.numberOfChannels, peak = 0, c, i;
        for (c = 0; c < ch; c++) { var d = ab.getChannelData(c); for (i = 0; i < n; i++) { var v = d[i] < 0 ? -d[i] : d[i]; if (v > peak) peak = v; } }
        var scale = Math.max(1, peak), k = 32767 / scale, data = [];
        for (c = 0; c < ch; c++) { var s = ab.getChannelData(c), q = new Int16Array(n); for (i = 0; i < n; i++) q[i] = Math.round(s[i] * k); data.push(q); }
        return { packed: true, data: data, scale: scale, length: n, numberOfChannels: ch, sampleRate: ab.sampleRate, duration: ab.duration };
    }
    function isPacked(x) { return !!(x && x.packed === true); }
    /** samples [a, b) of a packed stem as an AudioBuffer — the whole stem by default */
    function unpackStem(p, a, b) {
        a = a || 0; b = b == null ? p.length : Math.min(p.length, b);
        var out = makeBuffer(p.numberOfChannels, Math.max(1, b - a), p.sampleRate), k = p.scale / 32767;
        for (var c = 0; c < p.numberOfChannels; c++) { var q = p.data[c], d = out.getChannelData(c); for (var i = a; i < b; i++) d[i - a] = q[i] * k; }
        return out;
    }
    /** a stem as an AudioBuffer: a packed one is opened for the caller, who lets it go after use */
    function bufOf(x) { return isPacked(x) ? unpackStem(x) : x; }
    /** a packed stem opened as plain arrays — the input of ONE Signalsmith job, handed over without a copy */
    function openPlain(p) {
        var k = p.scale / 32767, data = [];
        for (var c = 0; c < p.numberOfChannels; c++) { var q = p.data[c], d = new Float32Array(p.length); for (var i = 0; i < p.length; i++) d[i] = q[i] * k; data.push(d); }
        return { numberOfChannels: p.numberOfChannels, length: p.length, sampleRate: p.sampleRate, duration: p.duration, getChannelData: function (c) { return data[c]; } };
    }
    /** Samples [t0, t1) of a stem, faded in over `fadeIn` s and out over `fadeOut` s (20 ms each by default). */
    function crop(ab, t0, t1, fadeIn, fadeOut) {
        var sr = ab.sampleRate, a = Math.max(0, Math.floor(t0 * sr)), b = Math.min(ab.length, Math.ceil(t1 * sr));
        // a packed stem opens only the region: the vocal is a part of the song
        var packed = isPacked(ab), out = packed ? unpackStem(ab, a, Math.max(a, b)) : makeBuffer(ab.numberOfChannels, Math.max(1, b - a), sr);
        var fi = Math.min(Math.floor((fadeIn > 0 ? fadeIn : 0.02) * sr), (b - a) >> 2), fo = Math.min(Math.floor((fadeOut > 0 ? fadeOut : 0.02) * sr), (b - a) >> 2);
        for (var c = 0; c < ab.numberOfChannels; c++) {
            var d = out.getChannelData(c), i;
            if (!packed) d.set(ab.getChannelData(c).subarray(a, b));
            for (i = 0; i < fi; i++) d[i] *= i / fi;
            for (i = 0; i < fo; i++) d[d.length - 1 - i] *= i / fo;
        }
        return out;
    }
    /**
     * One stretch/shift job with ONE retry when the Signalsmith worker went silent: its watchdog
     * kills a worker after 30 s without a progress beat (the worker beats every 2 % of output)
     * and the next call gets a fresh one. QA 24.09 lost a whole build at the render stage to a
     * single stalled job (signalsmith_worker_hung, the module download stalled on a loaded
     * machine) — two minutes of separation thrown away for one retryable call.
     */
    async function shiftSafe(src, o, signal) {
        var E = root.SignalsmithWorkerEngine;
        // short of memory: the worker goes after each render — an idle one held 125 MB of the studio
        // on the phone of the bug report of 2026-09-28 (signalsmith-engine.js `release`)
        if (lowMemoryDevice()) o = Object.assign({}, o, { release: true });
        // `src` may be a MAKER: a packed stem opened for this attempt only, its arrays handed to the worker
        // as they are (`transferInput`) — so the retry below opens it again
        var input = typeof src === 'function' ? src : function () { return src; };
        if (typeof src === 'function') o = Object.assign({}, o, { transferInput: true });
        try { return await E.shift(input(), o); }
        catch (e) {
            abort(signal);
            var c = String((e && (e.code || e.message)) || '');
            if (!/WORKER_HUNG|hung|worker/i.test(c)) throw e;
            try { root.__aeFeature && root.__aeFeature('mm_shift_retry', { code: (e && e.code) || null }); } catch (_) {}
            try { return await E.shift(input(), o); }
            catch (e2) { throw Object.assign(e2 instanceof Error ? e2 : new Error(String(e2)), { code: 'worker_hung' }); }
        }
    }
    function rms(ab) { var d = ab.getChannelData(0), s = 0, n = 0; for (var i = 0; i < d.length; i += 32) { s += d[i] * d[i]; n++; } return Math.sqrt(s / Math.max(1, n)); }

    /** A phone or a tablet — also one in «desktop site» mode, whose user agent says Linux x86_64
     *  (device-capabilities.js isHandheld; bug report 2026-09-28). */
    function handheld() {
        try { var c = root.__deviceCap && root.__deviceCap.get ? root.__deviceCap.get() : null; if (c) return !!(c.isMobile || c.isHandheld); } catch (_) {}
        var nav = root.navigator || {}, ua = nav.userAgent || '';
        return /Mobi|Android|iPhone|iPad/i.test(ua) || (/Macintosh/.test(ua) && nav.maxTouchPoints > 1);
    }
    /** 4 GB or less — or, where the browser does not say (Safari), a phone or a tablet. */
    function lowMemoryDevice() {
        var nav = root.navigator || {}, dm = nav.deviceMemory;
        return typeof dm === 'number' ? dm <= 4 : handheld();
    }

    /** Constant beat grid from a tempo and a first beat (a cappella, or a manual tempo). */
    function manualGrid(bpm, firstBeat, duration) { return root.MashupGrid.constantGrid(bpm, Math.max(0, firstBeat), duration, 4); }

    /**
     * The grid of a file with no drummer (an a cappella), at a tempo from `src`:
     *   'tap' / 'manual' — the person; 'name' — «… 128 BPM.wav»; 'tag' — ID3 TBPM / Vorbis BPM;
     *   'guess' — MashupGrid.combTempo over the whole file's beat activations.
     * The beats are a constant comb at that tempo, phased where the model's (faint) beat
     * activations line up best — or where the person tapped; the bars take ONE phase for the
     * whole take, where the model's downbeat head sums highest (see below). Without the
     * activations (an old worker) the phase is 0 and the bars start on the first beat.
     */
    function stemGrid(bpm, acts, duration, anchor) {
        var G = root.MashupGrid, fps = 50, period = 60 / bpm, phase;
        if (anchor != null) phase = ((anchor % period) + period) % period;
        else if (acts && acts.beat && acts.beat.length && G.combPhase) phase = G.combPhase(acts.beat, fps, bpm);
        else phase = 0;
        var g = G.constantGrid(bpm, phase, duration, 4);
        if (acts && acts.down && acts.down.length) {
            // ONE bar phase for the whole file: the downbeat head's evidence summed per phase. The
            // song Viterbi (barsFor) is wrong here — on a voice the downbeat probability is ~0.001
            // everywhere, every «downbeat» costs log(p/0.25) ≈ −5.5 against a 2.5 switch, and the
            // path dodges downbeats by switching: a real a cappella came back with 3 bars in 3:48
            // (QA 24.09). A constant-tempo take keeps its bar phase, so a global argmax is the model.
            var n = g.beats.length, D = acts.down, L = D.length, sum = [0, 0, 0, 0];
            for (var i = 0; i < n; i++) {
                var fr = Math.round(g.beats[i] * fps), mx = 0;
                for (var k = -1; k <= 1; k++) { var q = fr + k; if (q >= 0 && q < L) { var p = 1 / (1 + Math.exp(-D[q])); if (p > mx) mx = p; } }
                sum[i % 4] += mx;
            }
            var best = 0; for (var s = 1; s < 4; s++) if (sum[s] > sum[best]) best = s;
            var down = []; for (var j = best; j < n; j += 4) down.push(j);
            if (down.length >= 2) g.downIdx = Int32Array.from(down);
        }
        return g;
    }

    /** A usable model grid on a voice: bars spread over most of the file with some conviction. */
    function strongGrid(g, duration) {
        if (!g || !g.beats || g.beats.length < 16 || !(g.bpm > 0)) return false;
        var span = g.beats[g.beats.length - 1] - g.beats[0];
        return g.confidence >= 0.5 && span >= 0.6 * duration && g.downIdx && g.downIdx.length >= 8;
    }

    /** BPM from the file's own tags (ID3 TBPM, Vorbis BPM/TEMPO, MP4 freeform BPM). */
    function bpmFromTags(fields) {
        for (var i = 0; i < (fields || []).length; i++) {
            var f = fields[i], id = String(f.id || f.name || '').toUpperCase(), key = String(f.key || '').toUpperCase();
            if (id === 'TBPM' || id === 'TBP' || id === 'BPM' || id === 'TEMPO' || key === 'BPM' || key === 'TEMPO' || (id === '----' && /BPM/.test(key))) {
                var v = parseFloat(String(f.value || '').replace(',', '.'));
                if (v >= 50 && v <= 220) return v;
            }
        }
        return null;
    }

    /** Tempo from a file name: «Artist - Song (Acapella) 128 BPM.wav» → 128. */
    function bpmFromName(name) {
        var m = String(name || '').match(/(\d{2,3}(?:[.,]\d+)?)\s*bpm/i) || String(name || '').match(/bpm\s*(\d{2,3})/i);
        if (!m) return null;
        var v = parseFloat(m[1].replace(',', '.'));
        return v >= 50 && v <= 220 ? v : null;
    }

    /** Gate the vocal between phrases (−18 dB, 60 ms ramps): quiets separation leftovers where nobody sings. */
    function gateVocal(ab, phrases, shiftSec) {
        var sr = ab.sampleRate, out = makeBuffer(ab.numberOfChannels, ab.length, sr);
        var n = ab.length, env = new Float32Array(n), floor = Math.pow(10, -18 / 20), ramp = Math.floor(0.06 * sr);
        env.fill(floor);
        (phrases || []).forEach(function (p) {
            var a = Math.max(0, Math.floor((p.t0 + shiftSec) * sr) - ramp), b = Math.min(n, Math.ceil((p.t1 + shiftSec) * sr) + ramp);
            for (var i = a; i < b; i++) env[i] = 1;
        });
        // smooth the steps
        var y = floor, k = 1 / ramp;
        for (var i2 = 0; i2 < n; i2++) { y += (env[i2] - y) * k * 3; env[i2] = Math.min(1, y); }
        for (var c = 0; c < ab.numberOfChannels; c++) {
            var s = ab.getChannelData(c), d = out.getChannelData(c);
            for (var j = 0; j < n; j++) d[j] = s[j] * env[j];
        }
        return out;
    }

    /** Output time (relative to the vocal buffer's start) of A-time `t` through a rendered vocal's warp. */
    function warpOut(v, t) {
        var rel = v.rel, x = t - v.cropT0;
        if (x <= rel[0][0]) return rel[0][1] + (x - rel[0][0]);
        for (var i = 1; i < rel.length; i++) if (x <= rel[i][0]) {
            var f = (x - rel[i - 1][0]) / (rel[i][0] - rel[i - 1][0]);
            return rel[i - 1][1] + f * (rel[i][1] - rel[i - 1][1]);
        }
        var L = rel.length - 1; return rel[L][1] + (x - rel[L][0]);
    }
    /**
     * The mashup's 0:00 against the music's, from the singers on it — [{v: rendered vocal {at, cropT0,
     * rel}, phrases: [{t0, t1}] (A-time), offset: the person's nudge}]: where the first SUNG moment of
     * the earliest singer would fall before the music's start, how much later everything goes. A pickup
     * over a beat that starts on its first downbeat, or an a cappella trimmed tight to its first word,
     * started part-way into its buffer — that part was never heard (report 2026-10-06). 0 = no move.
     */
    function leadOf(singers) {
        var t = Infinity;
        (singers || []).forEach(function (s) {
            if (!s || !s.v || !s.v.rel || !s.v.rel.length) return;
            var ph = s.phrases || [];
            for (var i = 0; i < ph.length; i++) if (ph[i].t1 > s.v.cropT0) { t = Math.min(t, s.v.at + (s.offset || 0) + warpOut(s.v, Math.max(ph[i].t0, s.v.cropT0))); break; }
            // `from` — where a file that IS the vocal first sings (MashupPlan.sungFrom): a soft first line the
            // phrase detector opened late on is sung all the same, and is heard
            if (s.from != null) t = Math.min(t, s.v.at + (s.offset || 0) + warpOut(s.v, Math.max(s.from, s.v.cropT0)));
        });
        return isFinite(t) && t < 0 ? Math.round((0.05 - t) * 1000) / 1000 : 0;
    }

    function create(opts) {
        var slug = opts.slug || 'mashup-maker', ctx = opts.ctx;
        // (song) → Promise<AudioBuffer>: the page decodes a song's file again (releaseSongs)
        var redecode = typeof opts.redecode === 'function' ? opts.redecode : null;
        var S = { a: null, b: null, c: null }, plan = null, lastOpts = {}, rendered = null, outBeats = null;
        // the music as the timeline plays it when a vocal outlasts it: the file, its body going round,
        // its ending (MashupPlan.musicLoop, in B's own seconds); null — the music is played as it is
        var arrange = null;
        // the second vocal: its own plan and options; `cAuto` — C still takes the place the engine
        // gives it (right after A's fragment), so a change of A's fragment moves C along with it
        var planC = null, lastOptsC = {}, cAuto = true;

        function song(k) { return S[k]; }

        // Analysis by FILE, shared while it runs: a swap, a re-pick of the same file or the same song
        // in two slots attaches to the analysis already under way instead of paying for the beat
        // model twice. A song no slot wants any more is PARKED: it yields to every other song and is
        // dropped after a while (MashupAnalysis.job park) unless its file comes back — the reporter
        // of 2026-09-27 removed both songs after 7 minutes and picked the SAME files again; the first
        // run then kept the worker for results nobody would read and the second queued behind it.
        var analyses = {};                              // fileKey → { job, p, done, progress, listeners }
        var expected = { a: null, b: null, c: null };  // a file on its way into a slot (picked, decoding)
        function slotKey(x) { return expected[x] != null ? expected[x] : (S[x] && S[x].fileKey) || null; }
        function wanted(key) { return !!key && (slotKey('a') === key || slotKey('b') === key || slotKey('c') === key); }
        function tidy() {
            Object.keys(analyses).forEach(function (key) {
                var an = analyses[key];
                if (an.done) return;
                if (wanted(key)) { if (an.job.resume()) an.resumed = (an.resumed || 0) + 1; }
                else if (an.job.state() === 'live') { an.job.park(); an.parked = (an.parked || 0) + 1; }
            });
        }
        /** The page says which file is on its way into slot k (at the pick, before the decode). */
        function expect(k, fileKey) { expected[k] = fileKey || null; tidy(); }
        function startAnalysis(key, buf) {
            var J = root.MashupAnalysis.job({ prio: 1 });
            var an = { job: J, done: false, progress: [0, 0], listeners: {} };
            an.p = (async function () {
                var grid = await J.beats(buf, { keepActs: true, onProgress: function (d, n) {
                    an.progress = [d, n];
                    Object.keys(an.listeners).forEach(function (x) { try { an.listeners[x](d, n); } catch (_) {} });
                } });
                var mix = await J.mixFeat(buf);
                var acts = grid.acts || null; delete grid.acts;
                an.done = true;
                return { grid: grid, mix: mix, acts: acts };
            })();
            an.p.catch(function () { if (key && analyses[key] === an) delete analyses[key]; });
            if (key) analyses[key] = an;
            return an;
        }
        /** How far each slot's song is (for the page's progress): { k: [done, of] } of songs still in analysis. */
        function progress() {
            var out = {};
            ['a', 'b', 'c'].forEach(function (x) { var st = S[x], an = st && st.fileKey && analyses[st.fileKey]; if (st && !st.grid && an && !an.done) out[x] = an.progress.slice(); });
            return out;
        }

        /**
         * The song's sound is kept ONCE: as the file decoded it (its own rate and channels), and one
         * copy per FILE — the same file in two slots (B's song again as C's second singer) shares it.
         * Bug report 2026-09-28 (Android 13, 4 GB, three songs of 4–5 min, «страница вылетает», the
         * browser's own crash report: oom, six times): each song lived twice — the decoder's buffer
         * (held by the caller for the whole listening) and a copy at the context's rate made here —
         * and the same file twice over in B and C: 650 MB of PCM for 190 MB of sound, measured in
         * the page's renderer on her sequence. Nothing in the listening needs the context's rate;
         * the stems are brought to it when they are made (separate()), where the export needs it.
         */
        function sameFileSong(k, fileKey) {
            if (!fileKey) return null;
            var ks = ['a', 'b', 'c'];
            for (var i = 0; i < ks.length; i++) { var o = S[ks[i]]; if (ks[i] !== k && o && o.fileKey === fileKey && o.buffer) return o; }
            return null;
        }
        /** The decoded sound of a file already on the page (another slot), for the uploader to hand back
         *  instead of decoding the same file again. */
        function bufferFor(fileKey) { var o = sameFileSong(null, fileKey); return o ? o.buffer : null; }
        async function setSong(k, s) {
            var twin = sameFileSong(k, s.fileKey);
            var buf = twin ? twin.buffer : s.buffer;
            // `s` is the caller's object and may carry the decoded buffer: nothing below keeps it
            var onProgress = s.onProgress || null;
            var st = S[k] = { key: k, file: s.file || null, fileKey: s.fileKey || null, name: s.name || 'song', buffer: buf, duration: buf.duration, sampleRate: buf.sampleRate, channels: buf.numberOfChannels, isStem: !!s.isStem,
                grid: null, modelGrid: null, acts: null, mix: null, stems: null, feat: null,
                bpmHint: bpmFromName(s.name), tagBpm: s.tagBpm || null, tempoSrc: null, userTempo: null, analysis: null };
            s = null;
            if (expected[k] === st.fileKey || !st.fileKey) expected[k] = null;
            tidy();                                            // the slot's previous song may be nobody's now
            st.analysis = (async function () {
                var t0 = performance.now(), key = st.fileKey, r = null;
                for (var attempt = 0; ; attempt++) {
                    var an = key && analyses[key];
                    if (an) { st.cached = an.done; st.attached = !an.done; if (!an.done) an.job.resume(); }
                    else an = startAnalysis(key, st.buffer);
                    an.listeners[k] = function (d, n) { if (S[k] === st && onProgress) onProgress(d, n); };
                    if (an.progress[1] && onProgress) onProgress(an.progress[0], an.progress[1]);
                    try { r = await an.p; break; }
                    catch (e) {
                        // a graphics card that answered wrong after a song: every song runs again, once, on WASM
                        if (e && e.code === 'model_switched' && attempt < 1 && S[k] === st) continue;
                        throw e;
                    } finally { delete an.listeners[k]; }
                }
                if (S[k] !== st) return null;                                  // superseded by a newer file
                st.acts = r.acts;
                st.modelGrid = r.grid; st.grid = r.grid; st.mix = r.mix; st.analysisMs = Math.round(performance.now() - t0);
                if (st.isStem) applyStemTempo(st);
                twinTempo();
                return st;
            })();
            return st.analysis;
        }

        /**
         * The a cappella's tempo, most trusted first: what the person tapped or typed, the
         * file name, the file tags; a model grid that actually locked onto the voice; and only
         * then the comb's guess — shown as «≈, check» by the page. An INSTRUMENTAL keeps the
         * model grid (there is a drummer) unless the person typed a tempo.
         */
        function applyStemTempo(st) {
            var dur = st.duration;
            if (st.userTempo) { st.grid = stemGrid(st.userTempo.bpm, st.acts, dur, st.userTempo.anchor); st.tempoSrc = st.userTempo.src; return; }
            if (st.key === 'b') { st.grid = st.modelGrid; st.tempoSrc = 'model'; return; }
            if (st.bpmHint) { st.grid = stemGrid(st.bpmHint, st.acts, dur); st.tempoSrc = 'name'; return; }
            if (st.tagBpm) { st.grid = stemGrid(st.tagBpm, st.acts, dur); st.tempoSrc = 'tag'; return; }
            if (strongGrid(st.modelGrid, dur)) { st.grid = st.modelGrid; st.tempoSrc = 'model'; return; }
            var G = root.MashupGrid, c = st.acts && st.acts.beat && G.combTempo ? G.combTempo(st.acts.beat, 50) : null;
            var bpm = c && c.bpm > 0 ? c.bpm : (st.modelGrid && st.modelGrid.bpm > 0 ? st.modelGrid.bpm : 0);
            if (!bpm) { st.grid = st.modelGrid; st.tempoSrc = 'none'; return; }
            st.grid = stemGrid(bpm, st.acts, dur, c && c.bpm === bpm ? c.phase : null);
            st.tempoSrc = 'guess'; st.guess = c;
        }

        /**
         * An a cappella and a music file of ONE length are the stems of one song — exported together from
         * a project, or separated elsewhere — and so they share its beats: the singer's grid IS the music's,
         * and the vocal lands on the bar it was sung on (MashupPlan.plan: `twin`). A tempo guessed from the
         * voice has nothing to add to that. Field, 25.09–14.10.2026: 12 of 388 builds with an a cappella
         * file were such pairs (length within a second, tempo within 1 %), and on the 19 corpus songs their
         * own vocal landed a median of 3.1 s off its place — the landing was picked by harmony, the warp
         * by a guessed grid. Decided again whenever a song's analysis ends and at every build: the music
         * may be another file by then. A tempo the person typed or tapped stands.
         */
        function twinTempo() {
            var B = S.b;
            ['a', 'c'].forEach(function (k) {
                var X = S[k];
                if (!X || !X.isStem || !X.modelGrid) return;
                if (X.tempoSrc === 'twin') applyStemTempo(X);
                if (!X.userTempo && B && B.modelGrid && B.grid && B.grid.beats.length && X.duration >= 30 && Math.abs(X.duration - B.duration) <= 0.5) { X.grid = B.grid; X.tempoSrc = 'twin'; }
            });
        }

        /** «this file is an a cappella / an instrumental» turned on or off after the analysis. */
        function setIsStem(k, on) {
            var st = S[k]; if (!st) return;
            st.isStem = !!on; st.stems = null;
            if (st.isStem) applyStemTempo(st);
            else { st.grid = st.userTempo ? stemGrid(st.userTempo.bpm, st.acts, st.duration, st.userTempo.anchor) : st.modelGrid; st.tempoSrc = st.userTempo ? st.userTempo.src : 'model'; }
            twinTempo();
        }

        // the stems a slot keeps depend on the slot (A may hold its vocal alone): a swap separates again
        function swap() { var t = S.a; S.a = S.b; S.b = t; var e = expected.a; expected.a = expected.b; expected.b = e; [S.a, S.b].forEach(function (s, i) { if (s) { s.key = i ? 'b' : 'a'; s.stems = null; if (s.tempoSrc === 'twin') applyStemTempo(s); } }); plan = null; planC = null; rendered = null; arrange = null; }
        /** A new file is on its way into slot k: its old stems go now, not after the decode (a phone's memory). */
        function dropStems(k) { if (S[k]) { S[k].stems = null; S[k].feat = null; S[k].vfeat = null; } }
        /** The slot was emptied: the song leaves the engine (the third song: the mashup goes back to two). */
        function clear(k) { S[k] = null; expected[k] = null; tidy(); if (k === 'c') { planC = null; lastOptsC = {}; cAuto = true; if (rendered) rendered.vocal2 = null; } }
        /** Some song in the build still has to be separated (a device that cannot separate needs the server). */
        function needsSeparation() {
            return ['a', 'b', 'c'].some(function (k) {
                var st = S[k]; if (!st || st.isStem || !(st.buffer || st.released)) return false;
                var want = wantStems(k);
                return !(st.stems && want.every(function (w) { return !!st.stems[w]; }));
            });
        }

        /**
         * A tempo the person typed ('manual') or tapped ('tap', with the file time of one beat)
         * replaces whatever the grid was; the phase comes from the taps, else from the comb.
         */
        function setManualTempo(k, bpm, src, anchor) {
            var st = S[k]; if (!st || !(bpm >= 40 && bpm <= 240)) return;
            st.userTempo = { bpm: bpm, src: src || 'manual', anchor: anchor != null ? anchor : null };
            st.grid = stemGrid(bpm, st.acts, st.duration, st.userTempo.anchor);
            st.tempoSrc = st.userTempo.src;
        }
        /** ×2 / ÷2: a voice's tempo is most often found an octave off — one press, phase kept. */
        function scaleTempo(k, f) {
            var st = S[k]; if (!st || !st.grid || !(st.grid.bpm > 0)) return;
            var bpm = Math.round(st.grid.bpm * f * 100) / 100;
            if (!(bpm >= 40 && bpm <= 240)) return;
            setManualTempo(k, bpm, 'manual', st.grid.beats.length ? st.grid.beats[0] : null);
        }

        /**
         * The stems slot k needs from a separator — whichever one separates (the MUSION worker
         * here, or the server lane the page runs): A's vocal (and A's other stems for «turn A's
         * drums on», except on a device under 8 GB), all four of B. Empty for a file that IS the
         * stem. One answer for both roads, so the server is never asked for more than the device
         * would have kept, and a phone downloads five stems, not eight.
         */
        function wantStems(k) {
            var st = S[k];
            if (!st || st.isStem) return [];
            // the second singer gives only its voice: one stem, whatever the device
            if (k === 'c') return ['vocals'];
            var other = S[k === 'a' ? 'b' : 'a'], same = !!(other && !other.isStem && other.fileKey && other.fileKey === st.fileKey);
            // Safari (every iPhone and iPad) has no navigator.deviceMemory: «unknown» on a phone is the
            // SMALL case — the server road brings full songs to exactly these devices, and eight decoded
            // stereo stems of two 3.5-min songs (~600 MB) is over a phone tab's ceiling on their own
            var dm = (root.navigator || {}).deviceMemory;
            var small = typeof dm === 'number' ? dm < 8 : handheld();
            return k === 'a' && !same ? (small ? ['vocals'] : STEMS.slice()) : STEMS.slice();
        }

        /**
         * A PHONE lets go of the songs' own sound once the server has given their stems. After that
         * nothing in the studio reads it — the mix is made of stems, the plan of the grids, the length
         * is `duration` — and on the profile of the report of 2026-09-28 (4 GB, three songs, the server
         * road) the two songs were 190 MB of a studio that peaked at 1.3 GB. The file stays (a swap, a
         * song ticked «a cappella» after the build, a tempo check bring the sound back through
         * ensureBuffer). A video source keeps it: the server takes a video's sound as a WAV of it.
         */
        function isVideoFile(f) { return !!f && (/^video\//.test(f.type || '') || /\.(mp4|mov|m4v|webm|mkv|avi|3gp)$/i.test(f.name || '')); }
        function releaseSongs(fromServer) {
            if (!lowMemoryDevice() || !redecode) return;
            ['a', 'b', 'c'].forEach(function (k) {
                var st = S[k];
                if (!st || st.isStem || !st.buffer || !fromServer[k] || !st.stems || !st.file || isVideoFile(st.file)) return;
                var gone = st.buffer;
                // every slot holding this very buffer lets go of it together (the same file in B and C)
                ['a', 'b', 'c'].forEach(function (x) { var o = S[x]; if (o && o.buffer === gone && !o.isStem) { o.buffer = null; o.released = true; } });
            });
        }
        /**
         * A device short of memory keeps every stem the studio does not PLAY at 16 bits (packStem): the
         * singers' whole stems the vocals are cut from (crop opens only the region), the music song's own
         * voice, and the music's originals once their fitted copies are what sounds — 65 % of the builds
         * of 14 days moved the music's tempo or key (`tool_complete` stretch / i, RU, 01.10.2026), and then
         * the studio held the originals AND the copies: 1.54 GB on the phone profile of the bug report of
         * 2026-09-28. A stem the studio plays (rendered.bStems) stays a buffer; a packed one it starts to
         * play becomes one again (fitBStem).
         */
        async function packIdle() {
            if (!lowMemoryDevice()) return;
            var played = [];
            if (rendered) Object.keys(rendered.bStems || {}).forEach(function (k) { played.push(rendered.bStems[k]); });
            // and what the coming render will play as it is (the plan leaves its tempo and key alone)
            if (plan && S.b && S.b.stems) Object.keys(S.b.stems).forEach(function (k) {
                if ((neededB.indexOf(k) >= 0 || (S.b.isStem && k === 'other')) && fitOf(k).asIs) played.push(S.b.stems[k]);
            });
            var todo = [];
            ['a', 'b', 'c'].forEach(function (k) {
                var st = S[k]; if (!st || !st.stems || st.isStem) return;
                Object.keys(st.stems).forEach(function (n) { var x = st.stems[n]; if (x && !isPacked(x) && played.indexOf(x) < 0 && todo.indexOf(x) < 0) todo.push(x); });
            });
            for (var i = 0; i < todo.length; i++) {
                var x = todo[i], p = packStem(x);
                // every slot holding this very stem takes the packed one (C's voice is B's when C = B)
                ['a', 'b', 'c'].forEach(function (k) { var o = S[k]; if (o && o.stems) Object.keys(o.stems).forEach(function (n) { if (o.stems[n] === x) o.stems[n] = p; }); });
                await (root.__yieldTick ? root.__yieldTick(0) : Promise.resolve());
            }
        }
        /** The song's sound, decoded again from its file if the phone let go of it (releaseSongs). */
        var redecoding = {};
        function ensureBuffer(k) {
            var st = S[k];
            if (!st) return Promise.resolve(null);
            if (st.buffer) return Promise.resolve(st.buffer);
            if (!st.released || !redecode) return Promise.reject(Object.assign(new Error('song sound missing'), { code: 'song_missing' }));
            var key = st.fileKey || ('slot:' + k);
            if (!redecoding[key]) {
                redecoding[key] = Promise.resolve(redecode(st)).then(function (buf) {
                    delete redecoding[key];
                    if (!buf) throw Object.assign(new Error('song sound missing'), { code: 'song_missing' });
                    ['a', 'b', 'c'].forEach(function (x) { var o = S[x]; if (o && o.released && (o.fileKey || ('slot:' + x)) === key) { o.buffer = buf; o.released = false; } });
                    try { root.__aeFeature && root.__aeFeature('mm_song_redecoded', { k: k }); } catch (_) {}
                    return buf;
                }, function (e) { delete redecoding[key]; throw e; });
            }
            return redecoding[key];
        }
        /**
         * A song that IS the stem becomes one at the context's rate, in stereo — the rate every
         * separated stem arrives at (MUSION's and the server's are brought to it below), because the
         * export lays the tracks out by samples at one rate. The slot then keeps THAT copy as its song
         * (and so does a slot holding the same file): one copy, not the file's and the context's.
         */
        async function asStem(st) {
            await ensureBuffer(st.key);
            var old = st.buffer, own = stereo(await toRate(old, ctx.sampleRate));
            if (own !== old) ['a', 'b', 'c'].forEach(function (x) { if (S[x] && S[x].buffer === old) S[x].buffer = own; });
            return own;
        }
        /**
         * `external` — a promise of stems separated ELSEWHERE ({ name: AudioBuffer }), today the
         * server lane (mm-server.js). The engine does not know or care where: it takes the stems it
         * wants, at the context's rate, in stereo, exactly as it takes MUSION's.
         */
        async function separate(k, signal, onProgress, allowWasm, external, onBeat) {
            var st = S[k];
            if (st.stems) return st.stems;
            if (st.isStem) {
                var own = await asStem(st);
                abort(signal);
                st.stems = k === 'b' ? { other: own } : { vocals: own };
                return st.stems;
            }
            // C is the same file as A or B (a song with itself): its voice is already on the page
            if (k === 'c') {
                var twin = [S.a, S.b].filter(function (o) { return o && !o.isStem && o.fileKey && o.fileKey === st.fileKey && o.stems && o.stems.vocals; })[0];
                if (twin) { st.stems = { vocals: twin.stems.vocals }; return st.stems; }
            }
            // the same file in both slots (a remix of a song with itself, or a test) is separated
            // once: the second slot takes the first one's stems instead of another two minutes
            var other = S[k === 'a' ? 'b' : 'a'], same = !!(other && !other.isStem && other.fileKey && other.fileKey === st.fileKey);
            if (same && other.stems && STEMS.every(function (n) { return !!other.stems[n]; })) { st.stems = other.stems; return st.stems; }
            var want = wantStems(k), out;
            if (external) {
                out = await external;
                abort(signal);
                if (!out || !out.vocals) throw Object.assign(new Error('server stems missing'), { code: 'server_stems' });
            } else {
                var M = root.MusionStemSeparator;
                if (!M) throw Object.assign(new Error('separator missing'), { code: 'not_wired' });
                await ensureBuffer(k);
                // allowWasm: the person chose «separate on the processor» on a computer without WebGPU
                out = await M.split(st.buffer, { stems: want, signal: signal, slug: slug, onProgress: onProgress, onBeat: onBeat, allowWasm: !!allowWasm });
                abort(signal);
            }
            var stems = {};
            for (var i = 0; i < want.length; i++) if (out[want[i]]) stems[want[i]] = stereo(await toRate(out[want[i]], ctx.sampleRate));
            // short of memory, the server's stems are TAKEN: its result object is shared by the page's cache and
            // the build's own references, and a stem left in it stays at float whatever packIdle does (the page
            // reads an emptied result as «not cached» — mm-app srvCached)
            if (external && lowMemoryDevice()) want.forEach(function (w) { delete out[w]; });
            st.stems = stems;
            return stems;
        }

        async function features(signal) {
            var A = S.a, B = S.b, An = root.MashupAnalysis;
            var tuneA = A.mix ? A.mix.tuningCents : 0, tuneB = B.mix ? B.mix.tuningCents : 0;
            // bufOf: a rebuild finds the stems packed (packIdle) — each is opened for its feature and let go
            A.feat = await An.vocalFeat(bufOf(A.stems.vocals), tuneA); abort(signal);
            if (B.stems.bass && B.stems.other) B.feat = await An.bandFeat(bufOf(B.stems.bass), bufOf(B.stems.other));
            else { var bo = bufOf(B.stems.other); B.feat = await An.bandFeat(bo, bo); }
            abort(signal);
            B.vfeat = B.stems.vocals ? await An.vocalFeat(bufOf(B.stems.vocals), tuneB, true) : null;
            abort(signal);
            var C = S.c;
            if (C && C.stems && C.stems.vocals) { C.feat = await An.vocalFeat(bufOf(C.stems.vocals), C.mix ? C.mix.tuningCents : 0); abort(signal); }
            // A file the person calls an a cappella sings wherever it sounds: when the phrase detector
            // finds no line in it (vocal chops, a hum under its thresholds) the whole sounding span is
            // the one line — «almost no singing here» is an answer for a SEPARATED vocal only.
            [A, C].forEach(function (X) {
                if (X && X.isStem && X.feat && !X.feat.phrases.length && X.feat.sound && X.feat.sound.t1 - X.feat.sound.t0 >= 0.5) X.feat.phrases = [{ t0: X.feat.sound.t0, t1: X.feat.sound.t1 }];
            });
        }

        // `whole` — the file IS the vocal (the person ticked «this is an a cappella»): everything it
        // holds between the silence at its ends (`sound`) is the singer's, whatever the phrase detector
        // makes of it
        function singerOf(X) {
            return { grid: X.grid, vocal: X.feat.vocal, phrases: X.feat.phrases, voicedRatio: X.feat.voicedRatio,
                     tuningCents: X.mix ? X.mix.tuningCents : 0, duration: X.duration, whole: !!X.isStem, sound: X.feat.sound || null };
        }
        function bandOf(B) {
            return { grid: B.grid, harm: B.feat.harm, rms: B.mix ? B.mix.rms : null, phrases: B.vfeat ? B.vfeat.phrases : [],
                     tuningCents: B.feat.tuningCents, duration: B.duration };
        }
        function planNow(o) {
            lastOpts = Object.assign({}, lastOpts, o || {});
            plan = P().plan(singerOf(S.a), bandOf(S.b), lastOpts);
            return plan;
        }
        function hasC() { return !!(S.c && S.c.feat && S.c.stems && S.c.stems.vocals); }
        /** C's plan on A's music; `cAuto` keeps it right after A's fragment. */
        function planCNow(o) {
            if (!hasC() || !plan) { planC = null; return null; }
            lastOptsC = Object.assign({}, lastOptsC, o || {});
            var opts = Object.assign({}, lastOptsC);
            if (cAuto) { delete opts.forceBar; delete opts.forceBeat; opts.follow = true; }
            planC = P().planSecond(plan, singerOf(S.c), bandOf(S.b), opts);
            return planC;
        }

        /**
         * How the music goes on under the vocals of this plan: through to the end of the latest singer
         * (and a beat of their decay), the body from A's landing going round as many times as needed.
         * The vocal's end is counted in B-beats from the plan (`units`), so the decision comes BEFORE
         * the vocal is warped — the vocal is laid onto the grid of the music as it will sound.
         */
        function musicArrangement() {
            var B = S.b, Pn = P(), barsB = Pn.barsOf(B.grid), need = -Infinity;
            // `reach` — B-beats from the landing to the singer's last sound and its release (plan.extent). The
            // music goes round only for a vocal that really outlasts it — by more than a beat: until 2026-10-14
            // the count ran to the region's last bar line plus one beat, and a vocal that ended WITH the music
            // (an a cappella over its own instrumental) sent the music round for one more phrase.
            [plan, planC].forEach(function (pl) { if (pl && barsB[pl.o] && pl.units > 0) need = Math.max(need, barsB[pl.o].beat + (pl.db || 0) + (pl.reach != null ? pl.reach : pl.units + 1)); });
            if (!isFinite(need)) return null;
            var level = B.mix && B.mix.rms ? Pn.barRise(B.mix.rms.data, B.mix.rms.frameSec, barsB).level : null;
            return Pn.musicLoop(B.grid, B.duration, plan.o, need, { level: level, slack: 1 });
        }
        function arrangeSig(a) { return a ? a.l0 + ':' + a.l1 + ':' + a.inserted : ''; }
        /** Output timeline beats: B's own beats (going round, when the music repeats), scaled when the
         *  mashup plays at another tempo. */
        function timelineBeats() {
            var scale = plan.bpmB / plan.bpmOut, src = arrange ? arrange.beats : S.b.grid.beats;
            return Float64Array.from(src, function (t) { return t * scale; });
        }
        /** Bar starts on the output timeline — the repeated bars included. */
        function timelineBars() {
            if (!plan || !S.b) return [];
            var scale = plan.bpmB / plan.bpmOut, g = arrange || S.b.grid, pre = leadIn();
            return Array.prototype.map.call(g.downIdx, function (d) { return g.beats[d] * scale + pre; });
        }
        /** A singer's vocal (slot 'a' with `plan`, slot 'c' with `planC`) warped onto the music's beats. */
        async function renderVocalOf(k, pl, signal, lane) {
            var X = S[k], Pn = P();
            var barsB = Pn.barsOf(S.b.grid);
            var pairs = Pn.warpAnchors(pl, { grid: X.grid }, outBeats, barsB);
            if (pairs.length < 2) throw Object.assign(new Error('no anchors'), { code: 'no_anchors', which: k });
            // the vocal as the plan hears it (plan.extent): the first and the last anchor are its ends, in
            // the silence around the singing — an end the person put inside a line is faded, not clicked
            var cropT0 = pairs[0][0], cropT1 = Math.min(X.duration, pairs[pairs.length - 1][0]), ex = pl.extent || {};
            var vocalIn = crop(X.stems.vocals, cropT0, cropT1, ex.cut0 ? 0.08 : 0.02, ex.cut1 ? 0.08 : Math.min(0.25, (cropT1 - cropT0) / 8));
            var outT0 = pairs[0][1];
            var rel = pairs.map(function (p) { return [p[0] - cropT0, p[1] - outT0]; });
            var semis = pl.vocalShift, so = { semitones: semis, preserveFormants: Math.abs(semis) >= 0.5, warp: rel };
            if (lane) so.lane = lane;
            var outBuf = await shiftSafe(vocalIn, so, signal);
            abort(signal);
            // the same placement for A's other stems (only if the person turns them on — rendered lazily)
            return { buffer: outBuf, raw: outBuf, at: outT0, cropT0: cropT0, rel: rel, semis: semis };
        }
        function renderVocal(signal) { return renderVocalOf('a', plan, signal, 0); }

        // Only the stems that are ON are fitted: a tempo change re-stretches every one of
        // them (measured 47 s for four 3.7-min stems on the first e2e), and the music song's
        // own vocals are off by default. The rest are fitted when turned on (renderBStem).
        var neededB = ['drums', 'bass', 'other'];
        /**
         * How many Signalsmith workers this render may use at once (SignalsmithWorkerEngine
         * `lane`). A key or tempo change re-renders the vocal AND the music's pitched stems, and
         * on one worker they ran in a row: a 3.5-min pair took the sum of three renders. A
         * phone keeps one lane — every lane holds a whole stem in and out (~150 MB for 3.5 min
         * of stereo), and a phone's tab dies long before four of them fit.
         */
        function laneCount() {
            var E = root.SignalsmithWorkerEngine, max = (E && E.maxLanes) || 1;
            var nav = root.navigator || {}, hc = nav.hardwareConcurrency || 2, dm = nav.deviceMemory;
            if (handheld() || hc < 4 || (typeof dm === 'number' && dm < 4)) return 1;
            return Math.min(max, typeof dm === 'number' && dm < 8 ? 2 : 4);
        }
        /** What the plan does to a music stem: its tempo, its key (the drums keep theirs), or nothing. */
        function fitOf(kk) {
            var rate = plan.bpmOut / plan.bpmB, inst = plan.instShift;
            var pitched = (kk === 'bass' || kk === 'other') && inst !== 0, needRate = Math.abs(rate - 1) > 0.002;
            return { rate: rate, inst: inst, pitched: pitched, needRate: needRate, asIs: !pitched && !needRate };
        }
        async function fitBStem(kk, signal, laneIdx) {
            var B = S.b, src = B.stems[kk];
            if (!src) return null;
            var f = fitOf(kk), rate = f.rate, inst = f.inst, pitched = f.pitched, needRate = f.needRate;
            if (f.asIs) {
                // played as it is: a packed original becomes a buffer again, and stays one while it sounds
                if (isPacked(src)) { var open = unpackStem(src); B.stems[kk] = open; return open; }
                return src;
            }
            var out = await shiftSafe(isPacked(src) ? function () { return openPlain(src); } : src, { semitones: pitched ? inst : 0, rate: needRate ? rate : 1, lane: laneIdx || 0 }, signal);
            abort(signal);
            return out;
        }
        /** The music's stems — side by side on lanes 1…n-1 when there are lanes (lane 0 is the vocal's). */
        async function renderBStems(signal) {
            var out = {}, keys = Object.keys(S.b.stems).filter(function (kk) { return neededB.indexOf(kk) >= 0 || (S.b.isStem && kk === 'other'); });
            var n = laneCount();
            if (n < 2) {
                for (var i = 0; i < keys.length; i++) out[keys[i]] = await fitBStem(keys[i], signal, 0);
                return out;
            }
            var done = await Promise.all(keys.map(function (kk, j) { return fitBStem(kk, signal, 1 + (j % (n - 1))); }));
            keys.forEach(function (kk, j) { out[kk] = done[j]; });
            return out;
        }
        async function renderBStem(name, signal) {
            if (!rendered || !S.b.stems[name]) return null;
            if (neededB.indexOf(name) < 0) neededB.push(name);
            if (!rendered.bStems[name]) rendered.bStems[name] = await fitBStem(name, signal);
            return rendered.bStems[name];
        }

        async function renderAStem(name, signal) {
            var A = S.a;
            if (!A.stems[name] || !rendered || !rendered.vocal) return null;
            if (rendered.aStems[name]) return rendered.aStems[name];
            var v = rendered.vocal;
            var src = crop(A.stems[name], v.cropT0, v.cropT0 + (A.duration - v.cropT0));
            var semis = name === 'drums' ? 0 : v.semis;
            var b = await shiftSafe(src, { semitones: semis, warp: v.rel, lane: laneCount() > 1 ? 1 : 0 }, signal);
            abort(signal);
            rendered.aStems[name] = { buffer: b, at: v.at };
            return rendered.aStems[name];
        }

        function applyClean(on) {
            if (!rendered || !rendered.vocal) return;
            cleanOne(rendered.vocal, S.a, on);
            if (rendered.vocal2 && S.c) cleanOne(rendered.vocal2, S.c, on);
        }
        function cleanOne(v, X, on) {
            if (!on) { v.buffer = v.raw; return; }
            // the singer's phrases, moved onto the output timeline through the warp, relative to the vocal buffer start
            var map = function (t) { return warpOut(v, t); };
            var ph = (X.feat.phrases || []).map(function (p) { return { t0: map(p.t0), t1: map(p.t1) }; });
            v.buffer = gateVocal(v.raw, ph, 0);
        }

        async function render(signal, what) {
            what = what || { vocal: true, stems: true, vocal2: true };
            await packIdle();
            var prev = rendered;
            // the music's arrangement first: a vocal is warped onto the grid it will be heard over, so a
            // change in what repeats re-renders the vocals that were laid onto the previous one
            var arr = musicArrangement(), moved = !!prev && arrangeSig(arr) !== arrangeSig(arrange);
            arrange = arr;
            outBeats = timelineBeats();
            var wantV = what.vocal || !prev || moved, wantB = what.stems || !prev, v, b, c;
            var wantC = !!planC && (!!what.vocal2 || !prev || !prev.vocal2 || moved);
            var keepC = planC && prev ? prev.vocal2 : null, n = laneCount();
            if (n > 1) {
                // the vocal (lane 0), the music's stems (lanes 1…) and the second vocal (the last lane) at once
                var all = await Promise.all([wantV ? renderVocal(signal) : prev.vocal, wantB ? renderBStems(signal) : prev.bStems,
                    wantC ? renderVocalOf('c', planC, signal, n - 1) : keepC]);
                v = all[0]; b = all[1]; c = all[2];
            } else {
                v = wantV ? await renderVocal(signal) : prev.vocal;
                b = wantB ? await renderBStems(signal) : prev.bStems;
                c = wantC ? await renderVocalOf('c', planC, signal, 0) : keepC;
            }
            // A's other stems follow A's placement: kept only while A's vocal is the same render
            rendered = { vocal: v, bStems: b, aStems: prev && v === prev.vocal ? prev.aStems : {}, vocal2: c || null };
            await packIdle();
            return rendered;
        }

        // the person's fine nudge of the whole vocal (seconds, ± half a beat): a voice with no drummer
        // can land a hair early or late on its comb, and a singer's timing is taste as well. Applied
        // here, so the preview, the loop, the mashup file and the a cappella file all get the same.
        var vocalOffset = 0, vocalOffsetC = 0;
        /** How much later the whole mashup starts so the singers' first sung moment is heard (leadOf). */
        function leadIn() {
            if (!rendered || !rendered.vocal) return 0;
            var from = function (X) { return X && X.isStem && X.feat ? P().sungFrom({ phrases: X.feat.phrases, whole: true, sound: X.feat.sound }) : null; };
            return leadOf([{ v: rendered.vocal, phrases: S.a && S.a.feat ? S.a.feat.phrases : [], offset: vocalOffset, from: from(S.a) },
                           rendered.vocal2 && S.c ? { v: rendered.vocal2, phrases: S.c.feat ? S.c.feat.phrases : [], offset: vocalOffsetC, from: from(S.c) } : null]);
        }
        function tracks() {
            if (!rendered) return null;
            var pre = leadIn();
            var stems = {}, segs = null;
            if (arrange) {
                // the music stems are the file's own timeline (fitted to the tempo): the repeats are CLIPS of
                // them, not copies — a minute of music going round costs nothing (a phone, 2026-09-28)
                var sc = plan.bpmB / plan.bpmOut;
                segs = arrange.segs.map(function (g) { return { at: g.at * sc + pre, from: g.from * sc, dur: g.dur * sc }; });
            }
            Object.keys(rendered.bStems).forEach(function (k) { stems['b:' + k] = segs ? { buffer: rendered.bStems[k], at: pre, segs: segs } : { buffer: rendered.bStems[k], at: pre }; });
            Object.keys(rendered.aStems).forEach(function (k) { stems['a:' + k] = { buffer: rendered.aStems[k].buffer, at: rendered.aStems[k].at + vocalOffset + pre }; });
            // no clamp at 0: the mixer starts a track with a negative `at` part-way into its buffer, and
            // the vocal and A's other stems have to move together (a clamp on one of them splits them)
            var dur = 0, vAt = rendered.vocal.at + vocalOffset + pre;
            Object.keys(stems).forEach(function (k) { dur = Math.max(dur, root.MashupMixer ? root.MashupMixer.trackEnd(stems[k]) : stems[k].at + stems[k].buffer.duration); });
            dur = Math.max(dur, vAt + rendered.vocal.buffer.duration);
            var out = { vocal: { buffer: rendered.vocal.buffer, at: vAt }, stems: stems, duration: dur };
            if (rendered.vocal2) {
                var cAt = rendered.vocal2.at + vocalOffsetC + pre;
                out.vocal2 = { buffer: rendered.vocal2.buffer, at: cAt };
                out.duration = Math.max(out.duration, cAt + rendered.vocal2.buffer.duration);
            }
            return out;
        }

        async function build(bo) {
            bo = bo || {};
            var sig = bo.signal, stage = bo.onStage || function () {};
            // a C whose sound the phone let go of after the last build (releaseSongs) is still a song
            var A = S.a, B = S.b, C = bo.withC === false ? null : (S.c && (S.c.buffer || S.c.released) ? S.c : null);
            if (!A || !B) throw Object.assign(new Error('two songs needed'), { code: 'need_two' });
            await Promise.all([A.analysis, B.analysis, C ? C.analysis : null]);
            abort(sig);
            twinTempo();
            if (!A.grid || !A.grid.beats.length) throw Object.assign(new Error('no beat A'), { code: 'no_beat', which: 'a' });
            if (!B.grid || !B.grid.beats.length) throw Object.assign(new Error('no beat B'), { code: 'no_beat', which: 'b' });
            if (C && (!C.grid || !C.grid.beats.length)) throw Object.assign(new Error('no beat C'), { code: 'no_beat', which: 'c' });
            var t0 = performance.now(), times = {};
            stage('sep_a', 0, { name: A.name });
            var ext = bo.external || {};
            await separate('a', sig, function (p, t, d) { stage(p < 0.3 ? 'model' : 'sep_a', p, d || null); }, bo.allowWasm, ext.a, bo.onBeat);
            times.sepA = Math.round(performance.now() - t0);
            stage('sep_b', 0, { name: B.name });
            var t1 = performance.now();
            await separate('b', sig, function (p, t, d) { stage(p < 0.3 ? 'model' : 'sep_b', p, d || null); }, bo.allowWasm, ext.b, bo.onBeat);
            times.sepB = Math.round(performance.now() - t1);
            if (C) {
                stage('sep_c', 0, { name: C.name });
                var tc = performance.now();
                await separate('c', sig, function (p, t, d) { stage(p < 0.3 ? 'model' : 'sep_c', p, d || null); }, bo.allowWasm, ext.c, bo.onBeat);
                times.sepC = Math.round(performance.now() - tc);
            }
            try { if (root.MusionStemSeparator && root.MusionStemSeparator.release) root.MusionStemSeparator.release(); } catch (_) {}
            // a phone: the songs whose stems came from the server let go of their own sound (190 MB of
            // the 3-song studio on the report's profile) — before the features and the render need room
            releaseSongs({ a: !!ext.a, b: !!ext.b, c: !!ext.c });
            stage('melody', 0);
            var t2 = performance.now();
            await features(sig);
            times.feat = Math.round(performance.now() - t2);
            if (!A.feat.phrases.length) throw Object.assign(new Error('no vocals'), { code: 'no_vocals', which: 'a' });
            if (C && !(C.feat && C.feat.phrases.length)) throw Object.assign(new Error('no vocals C'), { code: 'no_vocals', which: 'c' });
            stage('plan', 0);
            // a build starts clean: the previous pair's forced key, bar and fragment belonged to other songs
            lastOpts = {}; lastOptsC = {}; planC = null; cAuto = true;
            planNow(bo.planOpts || {});
            if (!plan) throw Object.assign(new Error('no plan'), { code: 'no_plan' });
            if (C) {
                // two singers share the music: A the first half of what is left after its landing, C the rest
                var share = P().shareRegion(plan, P().barsOf(B.grid).length);
                if (share) planNow({ forceRegion: share, forceBar: plan.o });
                if (!planCNow({})) throw Object.assign(new Error('no plan C'), { code: 'no_plan', which: 'c' });
            }
            stage('render', 0);
            var t3 = performance.now();
            await render(sig);
            times.render = Math.round(performance.now() - t3);
            times.total = Math.round(performance.now() - t0);
            return { plan: plan, planC: planC, times: times };
        }

        async function replan(o, signal) {
            var before = plan, beforeC = planC, beforeOpts = lastOpts;
            planNow(o);
            // An edit no plan fits (a fragment cut shorter than a bar, a landing nothing scores) is refused
            // whole: the mashup that was there stays, with its options. Until 25.10.2026 `plan` went null
            // under the old C plan and the old tracks — the player and the key panel then read a null plan
            // (Sentry TIMBRICA-FRONTEND-RA/RB) and every later edit inherited the impossible option.
            if (!plan) { plan = before; lastOpts = beforeOpts; throw Object.assign(new Error('no plan'), { code: 'no_plan', which: 'a' }); }
            var stemsChanged = !before || before.instShift !== plan.instShift || before.bpmOut !== plan.bpmOut;
            // the second vocal follows the music's tempo and key and, while nobody moved it, A's end
            var cNew = false;
            if (hasC() && (stemsChanged || cAuto)) {
                planCNow({});
                cNew = !beforeC || !planC || stemsChanged || beforeC.o !== planC.o || beforeC.a0 !== planC.a0 || beforeC.a1 !== planC.a1 || beforeC.vocalShift !== planC.vocalShift;
            }
            await render(signal, { vocal: true, stems: stemsChanged, vocal2: cNew });
            return plan;
        }
        /** The second vocal alone: its key, landing, fragment. Moved or cut by hand, it stays where it is put. */
        async function replanC(o, signal) {
            if (!hasC() || !plan) return null;
            if (o && (o.forceBar != null || o.forceRegion || o.forceBeat)) cAuto = false;
            if (o && o.follow) { cAuto = true; o = Object.assign({}, o); delete o.follow; delete lastOptsC.forceBar; delete lastOptsC.forceBeat; delete lastOptsC.forceRegion; }
            var beforeC = planC, beforeOptsC = lastOptsC;
            planCNow(o);
            if (!planC) { planC = beforeC; lastOptsC = beforeOptsC; throw Object.assign(new Error('no plan C'), { code: 'no_plan', which: 'c' }); }
            await render(signal, { vocal: false, stems: false, vocal2: true });
            return planC;
        }

        /** The output time (before the lead-in) of the music's beat `j` in B's own count — fractional and
         *  negative too. Under a vocal that outlasts the music, the beats past the body are its repeats. */
        function outBeatT(j) {
            var Pn = P(), scale = plan.bpmB / plan.bpmOut;
            if (arrange && j >= arrange.insertBeat) return Pn.timeAtBeat(arrange.beats, j + arrange.inserted) * scale;
            return Pn.timeAtBeat(arrange ? arrange.beats : S.b.grid.beats, j) * scale;
        }
        /**
         * Move the vocal by whole bars and/or beats WITHOUT a re-render — what the person hears at once; the
         * page asks for the exact fit (replan with forceBar/forceBeat) a moment later. The beat offset a vocal
         * has stays when it moves by bars.
         */
        function nudgePreview(dBars, which, dBeats) {
            if (!rendered || !plan) return null;
            var isC = which === 'c', pl = isC ? planC : plan, v = isC ? rendered.vocal2 : rendered.vocal;
            if (!pl || !v) return null;
            var Pn = P(), barsB = Pn.barsOf(S.b.grid), db0 = pl.db || 0;
            var o2 = Math.max(0, Math.min(barsB.length - 1, pl.o + (dBars || 0)));
            var to = Pn.landing(barsB, o2, db0 + (dBeats || 0));
            var dt = outBeatT(barsB[to.o].beat + to.db) - outBeatT(barsB[pl.o].beat + db0);
            v.at += dt;
            if (!isC) Object.keys(rendered.aStems).forEach(function (k) { rendered.aStems[k].at += dt; });
            else cAuto = false;
            pl.o = to.o; pl.db = to.db; pl.landingB = { bar: to.o, beat: to.db, t: Pn.timeAtBeat(S.b.grid.beats, barsB[to.o].beat + to.db) };
            return pl;
        }
        /** Where a vocal stands, in the terms the page moves it by: bar o of the music + db beats + the fine
         *  nudge (s), its fragment [a0, a1) of nA bars, the meters, k, and the mashup's beat in seconds. */
        function placement(which) {
            var pl = which === 'c' ? planC : plan;
            if (!pl || !plan || !S.b || !S.b.grid) return null;
            return { o: pl.o, db: pl.db || 0, a0: pl.a0, a1: pl.a1, nA: pl.nBarsA, meterA: pl.meterA, meterB: pl.meterB, k: pl.k,
                     beatSec: 60 / plan.bpmOut, fine: which === 'c' ? vocalOffsetC : vocalOffset, nB: P().barsOf(S.b.grid).length };
        }

        return {
            setSong: setSong, swap: swap, song: song, setManualTempo: setManualTempo, scaleTempo: scaleTempo, setIsStem: setIsStem, wantStems: wantStems,
            build: build, replan: replan, replanC: replanC, nudgePreview: nudgePreview, placement: placement, renderAStem: renderAStem, renderBStem: renderBStem, applyClean: applyClean,
            setNeededB: function (names) { neededB = names.slice(); },
            setVocalOffset: function (sec, which) { var v = isFinite(sec) ? sec : 0; if (which === 'c') vocalOffsetC = v; else vocalOffset = v; },
            vocalOffset: function (which) { return which === 'c' ? vocalOffsetC : vocalOffset; },
            setContext: function (c) { ctx = c; },
            plan: function () { return plan; }, planC: function () { return planC; }, cAuto: function () { return cAuto; },
            // the options the plan in force was made with — what the page goes back to after a refused edit
            planOpts: function (which) { return Object.assign({}, which === 'c' ? lastOptsC : lastOpts); },
            dropStems: dropStems, clear: clear, needsSeparation: needsSeparation, expect: expect, progress: progress, bufferFor: bufferFor, ensureBuffer: ensureBuffer,
            lowMemory: function () { return lowMemoryDevice(); },
            // the beats as the timeline shows them (moved by the lead-in); the warp keeps the music's own
            tracks: tracks, timelineBeats: function () { var pre = leadIn(); return outBeats && pre ? Float64Array.from(outBeats, function (t) { return t + pre; }) : outBeats; }, timelineBars: timelineBars,
            leadIn: leadIn,
            // what of the music goes round, for the page: bars l0…l1 (0-based, l1 excluded) n more times
            arrangement: function () { return arrange ? { l0: arrange.l0, l1: arrange.l1, n: arrange.n } : null; },
            rendered: function () { return rendered; }, bpmFromName: bpmFromName,
            // the stems stay (they depend on the file alone): a rebuild after a tempo fix or with a third
            // song separates nothing twice; a slot taking a new file drops its own through dropStems()
            reset: function () {
                plan = null; planC = null; rendered = null; arrange = null; lastOpts = {}; lastOptsC = {}; cAuto = true; vocalOffset = 0; vocalOffsetC = 0;
                ['a', 'b', 'c'].forEach(function (k) { if (S[k]) { S[k].feat = null; S[k].vfeat = null; } });
            }
        };
    }

    root.MashupEngine = { create: create, bpmFromName: bpmFromName, bpmFromTags: bpmFromTags, gateVocal: gateVocal, stemGrid: stemGrid, strongGrid: strongGrid, leadOf: leadOf, warpOut: warpOut,
        packStem: packStem, unpackStem: unpackStem, crop: crop };
})(typeof window !== 'undefined' ? window : self);
